import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { FormModal } from "@/components/pawkt/FormModal";
import { Button } from "@/components/ui/button";
import { Ticket, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { name: "code", label: "Code", placeholder: "SAVE20", full: true },
  { name: "kind", label: "Type", type: "select", options: [{ value: "percentage", label: "Percentage" }, { value: "flat", label: "Flat ₹" }] },
  { name: "value", label: "Value", type: "number" },
  { name: "min_purchase", label: "Min purchase (₹)", type: "number" },
  { name: "usage_limit", label: "Total usage limit (0=∞)", type: "number" },
  { name: "per_customer_limit", label: "Per-customer limit (0=∞)", type: "number" },
  { name: "valid_to", label: "Valid until (YYYY-MM-DD)", type: "text", full: true },
  { name: "active", label: "Active", type: "select", options: [{ value: true, label: "Active" }, { value: false, label: "Inactive" }] },
];

export default function Coupons() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["coupons"], queryFn: async () => (await api.get("/coupons")).data });
  const add = useMutation({ mutationFn: (b) => api.post("/coupons", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["coupons"] }); setOpen(false); toast.success("Coupon created"); }, onError: (e) => toast.error(apiErr(e)) });
  const upd = useMutation({ mutationFn: ({ id, b }) => api.put(`/coupons/${id}`, b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["coupons"] }); setEditRow(null); toast.success("Coupon updated"); }, onError: (e) => toast.error(apiErr(e)) });
  const del = useMutation({ mutationFn: (id) => api.delete(`/coupons/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["coupons"] }) });

  return (
    <div>
      <PageHeader title="Coupons" subtitle="Discounts computed & validated server-side">
        <Button onClick={() => setOpen(true)} data-testid="add-coupon-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> New Coupon</Button>
      </PageHeader>
      {isLoading ? <Loading /> : (data?.length ? (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Value</th><th className="px-4 py-3">Min spend</th><th className="px-4 py-3">Used</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`coupon-${c.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
                  <td className="px-4 py-3">{c.kind === "percentage" ? `${c.value}%` : inr(c.value)}</td>
                  <td className="px-4 py-3">{inr(c.min_purchase)}</td>
                  <td className="px-4 py-3 tabular">{c.used_count}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                  <td className="px-4 py-3">{c.active === false ? <span className="text-slate-400">Inactive</span> : <span className="text-emerald-600">Active</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditRow(c)} data-testid={`edit-coupon-${c.id}`} className="text-slate-500 hover:bg-slate-100 rounded p-1 mr-1"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del.mutate(c.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty icon={Ticket} title="No coupons" subtitle="Create % or flat discounts with conditions." />)}

      <FormModal open={open} onOpenChange={setOpen} title="New Coupon" testid="coupon-modal"
        initial={{ kind: "percentage", value: 10, min_purchase: 0, active: true }} submitLabel="Create coupon"
        fields={FIELDS} onSubmit={(v) => add.mutateAsync(v)} />

      <FormModal open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)} title={`Edit ${editRow?.code || "Coupon"}`} testid="coupon-edit-modal"
        initial={editRow ? { code: editRow.code, kind: editRow.kind, value: editRow.value, min_purchase: editRow.min_purchase, usage_limit: editRow.usage_limit || 0, per_customer_limit: editRow.per_customer_limit || 0, valid_to: editRow.valid_to || "", active: editRow.active !== false } : {}}
        submitLabel="Save changes" fields={FIELDS} onSubmit={(v) => upd.mutateAsync({ id: editRow.id, b: v })} />
    </div>
  );
}
